"use client";
import { GridItem } from "@ui/components/layout/grid/Grid";
import type { BaseHit, Hit } from "instantsearch.js";
import type { BaseAutocompleteItemProps, QuerySuggestionCollectionValue } from "./types";
import { Text } from "@ui/components/content/text/Text";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useMemo } from "react";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { NextLink } from "@components/ui/next-link/NextLink";
import { Highlight } from "react-instantsearch";
import type { HistoryItem } from "@algolia/autocomplete-plugin-recent-searches/dist/esm/types";
import { QUERY_SUGGESTIONS_PLUGIN } from "@e2e/helpers/homepage/constants";
import {
    GRID_AREAS,
    querySuggestionItemLinkHighlightStyles,
    querySuggestionItemLinkStyles,
    querySuggestionMarkHighlightStyles,
    querySuggestionsListStyles,
} from "./AutoCompletePanelStyles";
import { useLocale } from "next-intl";
import { getIndexName } from "@lib/wrappers/algolia";
import { Box } from "@ui/components/layout/box/Box";
import { defaultUnderlineStyle } from "@ui/css/common";
import { HighlightedText } from "./HighlightedText";

export interface QuerySuggestionsGridProps
    extends BaseAutocompleteItemProps<QuerySuggestionCollectionValue> {
    onResultClick?: (item: HistoryItem) => void;
    totalHits?: number;
}

export const QuerySuggestionsGrid = ({
    autocomplete,
    collection,
    query,
    totalHits,
    onResultClick,
}: QuerySuggestionsGridProps) => {
    const { items } = collection || {};
    const locale = useLocale();
    const indexName = getIndexName(locale, "products");
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.searchBarRecentSearches,
        CommonCMS.searchBarPopularSearches,
        CommonCMS.searchBarPanelLabel,
    ]);

    const allResultsMicrocopyKey =
        totalHits === 1 ? CommonCMS.search.seeAllSingular : CommonCMS.search.seeAllPlural;

    const getAllResultsText = getMicrocopy(CommonCMS.shared, allResultsMicrocopyKey);

    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);

    const shouldShowAllText = query && typeof totalHits !== "undefined";

    return (
        <GridItem
            data-testid={QUERY_SUGGESTIONS_PLUGIN}
            display="flex"
            flexDirection="column"
            gridArea={GRID_AREAS.suggestions}
            {...autocomplete?.getListProps()}
        >
            <Box {...querySuggestionsListStyles}>
                {!query && (
                    <Text
                        tabIndex={0}
                        fontSize="desktopBodyTextSmall"
                        fontWeight="bold"
                        textTransform="uppercase"
                        data-testid="autocompleteSearchesHeader"
                    >
                        {microcopies[CommonCMS.searchBarPopularSearches]}
                    </Text>
                )}
                <NextLink
                    onClick={() => onResultClick?.({ id: query, label: query })}
                    href={`${searchPagePath}?query=${encodeURIComponent(query)}`}
                    // Highlight can't be applied as an inline style because it relies on child selectors.
                    sx={{
                        "& mark": {
                            fontWeight: "bold",
                            backgroundColor: "transparent",
                        },
                    }}
                    // For the first search suggestions can be faster than the actual result so we show an empty space to avoid layout shift
                    opacity={shouldShowAllText ? 1 : 0}
                    {...defaultUnderlineStyle}
                    fontWeight="normal"
                    color="black"
                >
                    <HighlightedText
                        text={getAllResultsText}
                        highlights={{ results: totalHits?.toString(), term: `“${query}”` }}
                    />
                </NextLink>

                {items?.map((item) => {
                    const itemHitCount = item?.[indexName]?.exact_nb_hits;
                    /*
					const hasItemHitCount =
					itemHitCount !== null && typeof itemHitCount !== "undefined";
					*/
                    /*
					The Query Suggestions index in Algolia is currently not able to take hasStock:true into account.
					This means that the itemHitCount shows how many results there are regardless of stock status.
					The Search results page shows results taking stock status into account, causing a mismatch between this number and the actual results.
					Disabled until this can be fixed.
					*/
                    const hasItemHitCount = false;
                    return (
                        <NextLink
                            // Highlight can't be applied as an inline style because it relies on child selectors.
                            sx={{
                                ...querySuggestionItemLinkHighlightStyles,
                                ...querySuggestionMarkHighlightStyles,
                            }}
                            {...querySuggestionItemLinkStyles}
                            href={`${searchPagePath}?query=${encodeURIComponent(item.query)}`}
                            fontSize={query ? "desktopBodyTextSmall" : "desktopHeading5"}
                            key={`${item.objectID}-${item.query}`}
                            onClick={() =>
                                onResultClick?.({
                                    id: item.query,
                                    label: item.query,
                                })
                            }
                        >
                            <Highlight attribute="query" hit={item as any as Hit<BaseHit>} />
                            {hasItemHitCount && (
                                <Text
                                    as="span"
                                    fontSize="desktopBodyTextSmall"
                                    color="textSecondary"
                                    ml="auto"
                                >
                                    ({itemHitCount})
                                </Text>
                            )}
                        </NextLink>
                    );
                })}
            </Box>
        </GridItem>
    );
};
